import { useBundle } from '@amzn/react-arb-tools';
import { BENTO_CARD_VARIANT, HEADING_TAG, SECTION_THEME, useDeviceSize } from '@amzn/sss-website-theme';

import { SegmentConfigProps } from 'src/components/pages/segment/types';
import { SUPERPOLLUTANTS_BUNDLE_NAME } from 'src/constants/panther/resource-bundles';
import { getImageRoot } from 'src/helpers';
import { t } from 'src/helpers/translations/translation-helpers';

export function useSuperpollutantsConfig(): { isBundleLoading: boolean; config: SegmentConfigProps } {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  const [superpollutantsBundle, isSuperpollutantsBundleLoading] = useBundle(SUPERPOLLUTANTS_BUNDLE_NAME);

  return {
    isBundleLoading: isSuperpollutantsBundleLoading,
    config: {
      heroAsset: {
        image: {
          src: `${imageRoot}/sp-hero.jpg`,
          alt: t(superpollutantsBundle, 'heroAsset_image_altText'),
        },
        title: t(superpollutantsBundle, 'heroAsset_title'),
        subtitle: t(superpollutantsBundle, 'heroAsset_subtitle'),
      },

      headingSection: {
        tagline: t(superpollutantsBundle, 'headingSection_tagline'),
        heading: t(superpollutantsBundle, 'headingSection_heading'),
        description: t(superpollutantsBundle, 'headingSection_description'),
      },

      cardsDashboard: {
        ...(isSmallDesktop
          ? {
              firstCard: {
                src: `${imageRoot}/sp-challenge-1.jpg`,
                alt: t(superpollutantsBundle, 'cardsDashboard_firstCard_altText'),
              },
            }
          : {}),
        secondCard: {
          image: undefined,
          label: t(superpollutantsBundle, 'cardsDashboard_secondCard_label'),
          title: t(superpollutantsBundle, 'cardsDashboard_secondCard_title'),
          description: t(superpollutantsBundle, 'cardsDashboard_secondCard_description'),
          variant: BENTO_CARD_VARIANT.STANDARD,
        },
        thirdCard: {
          label: t(superpollutantsBundle, 'cardsDashboard_thirdCard_label'),
          title: t(superpollutantsBundle, 'cardsDashboard_thirdCard_title'),
          description: t(superpollutantsBundle, 'cardsDashboard_thirdCard_description'),
        },
        ...(isSmallDesktop
          ? {
              fourthCard: {
                src: `${imageRoot}/sp-challenge-2.jpg`,
                alt: t(superpollutantsBundle, 'cardsDashboard_fourthCard_altText'),
              },
            }
          : {}),
      },

      dynamicList: {
        mediaProps: {
          mediaTitle: t(superpollutantsBundle, 'dynamicList_mediaTitle'),
          mediaTagline: t(superpollutantsBundle, 'dynamicList_mediaTagline'),
          media: [
            {
              image: {
                src: `${imageRoot}/sp-approach-1.jpg`,
                alt: t(superpollutantsBundle, 'dynamicList_firstMedia_altText'),
              },
            },
          ],
        },
        items: [
          {
            title: t(superpollutantsBundle, 'dynamicList_firstItem_title'),
            body: t(superpollutantsBundle, 'dynamicList_firstItem_body'),
          },
        ],
      },

      secondaryBentoCards: {
        firstCard: `${imageRoot}/sp-quality-1.jpg`,
        secondCard: `${imageRoot}/sp-quality-2.jpg`,

        bentoCardProps: {
          label: t(superpollutantsBundle, 'secondaryBentoCards_label'),
          title: t(superpollutantsBundle, 'secondaryBentoCards_title'),
          titleSize: HEADING_TAG.H4,
          description: t(superpollutantsBundle, 'secondaryBentoCards_description'),
          tooltipAttributes: [
            {
              description: t(superpollutantsBundle, 'secondaryBentoCards_tooltip_description'),
            },
          ],
          tooltipTheme: SECTION_THEME.Light,
          firstCard: {
            src: `${imageRoot}/sp-quality-1.jpg`,
            alt: t(superpollutantsBundle, 'secondaryBentoCards_firstCard_altText'),
          },
          secondCard: {
            src: `${imageRoot}/sp-quality-2.jpg`,
            alt: t(superpollutantsBundle, 'secondaryBentoCards_secondCard_altText'),
          },
        },
      },
    },
  };
}
